/**
 * 即梦生图节点 - 基于即梦4.0，支持文生图和图生图两种模式
 *
 * 功能特性：
 * 1. 文生图模式：仅通过文本提示词生成图像
 * 2. 图生图模式：基于参考图片和提示词生成图像
 * 3. 自动模式识别：根据是否有图片参考自动切换模式
 * 4. 支持多图参考（1-10张）
 * 5. 支持拖拽排序参考图片
 * 6. 实时显示连接状态和参考图片
 */
import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './ImengImageGenNode.css';

const apiService = new ApiService();

const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const mergeImageLists = (prev = [], next = []) => {
  const nextQueue = [...next];
  const reordered = [];

  prev.forEach((url) => {
    const index = nextQueue.indexOf(url);
    if (index !== -1) {
      reordered.push(url);
      nextQueue.splice(index, 1);
    }
  });

  return [...reordered, ...nextQueue];
};

export const ImengImageGenNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();

  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || 'landscape_16_9');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImages, setConnectedImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
    });
  }, [id, aspectRatio, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = useCallback(() => {
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;

      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'prompt') return true;

      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        return sourceNode?.type === 'promptInput';
      }

      return false;
    });

    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });

    return prompts;
  }, [edges, getNode, id]);

  // 获取连接到此节点的所有图片参考节点和任务节点
  const getConnectedImageNodes = useCallback(() => {
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;

      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'image') return true;

      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'imageDisplay'];
        return validImageTypes.includes(sourceNode?.type);
      }

      return false;
    });

    console.log(`ImengImageGenNode ${id} - 连接到图片输入的边:`, connectedEdges.length);

    const imageUrls = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`ImengImageGenNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);

      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        // 过滤掉本地文件URL（data: 或 blob:）
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`ImengImageGenNode ${id} - 从图片参考节点获取图片:`, url);
        }
      }

      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        // 只使用已上传到云端的图片，过滤掉本地 base64 预览
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`ImengImageGenNode ${id} - 从手绘节点获取图片:`, url);
        } else if (sourceNode.data?.isLocalPreview) {
          console.warn(`ImengImageGenNode ${id} - 手绘节点图片为本地预览，跳过`);
        }
      }

      // 从图片展示节点获取图片
      if (sourceNode && sourceNode.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`ImengImageGenNode ${id} - 从图片展示节点获取图片:`, url);
        }
      }

      // 从图片任务节点获取生成的图片
      if (sourceNode && sourceNode.type === 'imageTask') {
        console.log(`ImengImageGenNode ${id} - 任务节点数据:`, {
          status: sourceNode.data?.status,
          generatedImages: sourceNode.data?.generatedImages,
          imagesCount: sourceNode.data?.generatedImages?.length
        });

        if (sourceNode.data?.generatedImages) {
          const images = sourceNode.data.generatedImages;
          if (Array.isArray(images)) {
            images.forEach(img => {
              const url = typeof img === 'string' ? img : img.url;
              if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
                imageUrls.push(url);
                console.log(`ImengImageGenNode ${id} - 从任务节点获取图片:`, url);
              }
            });
          }
        }
      }
    });

    console.log(`ImengImageGenNode ${id} - 总共获取到 ${imageUrls.length} 张图片`);
    return imageUrls;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片列表
  useEffect(() => {
    const imageUrls = getConnectedImageNodes();
    setConnectedImages((prev) => {
      const merged = mergeImageLists(prev, imageUrls);
      return arraysEqual(prev, merged) ? prev : merged;
    });
  }, [getConnectedImageNodes]);

  // 处理拖动开始
  const handleDragStart = (e, index) => {
    if (readOnly) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  // 处理拖动结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 处理拖动经过
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // 处理放置
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || readOnly) return;

    const newImages = [...connectedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setConnectedImages(newImages);
    setDraggedIndex(null);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);

      // 1. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }

      const prompt = prompts.join('\n'); // 合并多个提示词
      console.log('使用提示词:', prompt);

      // 2. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }

      // 3. 确定生成模式和参数
      const imageUrls = connectedImages;
      const isImageToImage = imageUrls.length > 0;

      // 根据是否有图片选择不同的 app_name
      const appName = isImageToImage 
        ? 'fal-ai/bytedance/seedream/v4/edit'
        : 'fal-ai/bytedance/seedream/v4/text-to-image';

      let inputParams = {
        prompt: prompt,
        aspect_ratio: aspectRatio,
      };

      // 图生图模式：添加参考图片参数
      if (isImageToImage) {
        if (imageUrls.length > 10) {
          setError('最多支持10张参考图片');
          setIsGenerating(false);
          return;
        }

        inputParams.image_urls = imageUrls;
        console.log('图生图模式，使用参考图片:', imageUrls);
      } else {
        console.log('文生图模式');
      }

      // 4. 调用即梦4.0 API创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          input_params: inputParams
        }
      );

      console.log('任务创建响应:', response);

      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        const taskType = isImageToImage ? 'imeng-image-to-image' : 'imeng-text-to-image';
        toast.success(`任务已创建！${isImageToImage ? `使用 ${imageUrls.length} 张参考图片` : ''}`);

        // 5. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450, // 在右侧450px处
          y: currentNode.position.y,
        };

        // 6. 创建 ImageTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'imageTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: taskType,
            status: 'pending',
            inputParams: inputParams,
            generatedImages: [],
          },
        };

        // 7. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id, // 当前即梦生图节点
          target: taskNodeId, // 新创建的任务节点
          type: 'default',
        };

        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建任务节点和连接线:', taskNodeId);

        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }

    } catch (err) {
      console.error('即梦生图失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }

    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'imageDisplay'];
      return validImageTypes.includes(sourceNode?.type);
    }

    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'imengImageGen', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 确定当前模式
  const isImageToImageMode = connectedImages.length > 0;

  return (
    <div className="ImengImageGenNode-container">
      {/* 复制按钮 */}
      <button
        className="ImengImageGenNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="ImengImageGenNode-handle ImengImageGenNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '30%' }}
      >
        <div 
          className="ImengImageGenNode-handle-label ImengImageGenNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="ImengImageGenNode-handle ImengImageGenNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '70%' }}
      >
        <div 
          className="ImengImageGenNode-handle-label ImengImageGenNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多4张）"
        >
          参考图
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="ImengImageGenNode-handle ImengImageGenNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="ImengImageGenNode-title">
        即梦生图（4.0）
        <span className={`ImengImageGenNode-mode-badge ${isImageToImageMode ? 'ImengImageGenNode-mode-i2i' : 'ImengImageGenNode-mode-t2i'}`}>
          {isImageToImageMode ? '图生图' : '文生图'}
        </span>
      </div>

      {/* 内容区域 */}
      <div className="ImengImageGenNode-wrapper">
        {/* 宽高比 */}
        <div className="ImengImageGenNode-field">
          <label className="ImengImageGenNode-label">宽高比</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={isGenerating || readOnly}
            className="ImengImageGenNode-select"
          >
            <option value="square_hd">1:1 正方形</option>
            <option value="landscape_16_9">16:9 横屏</option>
            <option value="portrait_16_9">9:16 竖屏</option>
            <option value="portrait_4_3">4:3 传统</option>
            <option value="landscape_4_3">3:4 竖版</option>
          </select>
        </div>

        {/* 参考图片展示 */}
        {connectedImages.length > 0 && (
          <div className="ImengImageGenNode-field">
            <label className="ImengImageGenNode-label">
              参考图片 ({connectedImages.length}/10)
            </label>
            <div className="ImengImageGenNode-image-grid nodrag">
              {connectedImages.map((imageUrl, index) => (
                <div
                  key={`${imageUrl}-${index}`}
                  className={`ImengImageGenNode-image-thumb nodrag ${
                    draggedIndex === index ? 'ImengImageGenNode-image-dragging' : ''
                  }`}
                  draggable={!readOnly}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <img
                    src={imageUrl}
                    alt={`参考图 ${index + 1}`}
                    className="ImengImageGenNode-thumb-img"
                  />
                  <div className="ImengImageGenNode-thumb-index">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 连接提示 */}
        <div className="ImengImageGenNode-hint">
          💡 <strong>文生图模式：</strong>仅连接文本输入节点<br />
          🎨 <strong>图生图模式：</strong>连接文本输入 + 图片参考节点<br />
          📋 支持多图参考（1-10张），拖拽可排序<br />
          任务节点的生成图片可作为新的参考图<br />
          点击生成后会自动创建任务节点展示结果
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`ImengImageGenNode-generate ${isGenerating || readOnly ? 'ImengImageGenNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="ImengImageGenNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

ImengImageGenNode.displayName = 'ImengImageGenNode';
